import { Loader } from '@mantine/core';
import { IconCheck, IconInfoTriangle, IconX } from '@tabler/icons-react';
import type { CSSProperties, ReactNode } from 'react';
import { use } from 'react';

import { mantineColorVar } from '#core/lib/formatters/colors.ts';

import { ToolChromeContext } from './ToolChromeContext.ts';
import classes from './ToolDisplay.module.css';
import { Shimmer } from '#plugin-chat/components/shared/Shimmer.tsx';
import { useToolStatusKind } from '#plugin-chat/hooks/usePartStatus.ts';
import { useToolLabel } from '#plugin-chat/hooks/useToolLabel.ts';
import { useToolPart } from '#plugin-chat/hooks/useToolPart.ts';
import type { StyledIcon } from '#plugin-chat/lib/tool/message-formatters.ts';
import { parseResultEnvelope } from '#plugin-chat/lib/tool/result-envelope.ts';

/** Complete-row colors derive from the tool color at this shade. */
const TOOL_COLOR_SHADE = 4;

export interface ToolStatusRowProps {
  /** Override the derived status icon. */
  icon?: StyledIcon;
  /** Override the derived icon/label color (CSS color value). */
  color?: string;
  /** Trailing row content next to the label (badges, counts). */
  extras?: ReactNode;
  /** Override the conventional i18n label (`tools.<name>.<status>`). */
  children?: ReactNode;
}

/**
 * The chrome's status row: icon cell + label cell inside the tool-display
 * grid. Self-derives everything from the chrome scope and the active
 * status kind — icon, color, shimmer-while-running, and the conventional
 * i18n label — so the zero-props mount is the complete default row.
 * Status renderers compose it for row-shaped overrides.
 */
export function ToolStatusRow({ icon, color, extras, children }: ToolStatusRowProps) {
  const kind = useToolStatusKind();
  const chrome = use(ToolChromeContext);
  const { args = {}, result } = useToolPart<Record<string, unknown>, unknown>();
  const toolLabel = useToolLabel();

  const envelope = parseResultEnvelope(result);
  const label =
    children ??
    toolLabel(chrome.toolName, kind, {
      ...chrome.labelValues?.({ args, result }),
      ...(envelope.kind === 'failure' && { error: envelope.errorMessage }),
    });

  const isPending = kind === 'running' || kind === 'awaitingInput';
  const resolved = statusPresentation(kind, chrome);
  const IconComponent = icon ?? resolved.icon;
  const iconColor = color ?? resolved.color;

  return (
    <>
      <div className={classes.icon}>
        {isPending ? <Loader color="dark.1" size={16} /> : <IconComponent color={iconColor} size={16} />}
      </div>
      <div
        className={classes.label}
        style={kind === 'complete' ? ({ '--status-color': iconColor } as CSSProperties) : undefined}
      >
        {isPending ? (
          <Shimmer color="color-mix(in oklab, var(--mantine-color-dark-3), transparent 80%)" variant="background">
            {label}
          </Shimmer>
        ) : (
          label
        )}
        {extras}
      </div>
    </>
  );
}

/**
 * Default icon + color per status kind. The complete row picks up the
 * tool's declared color/icon; the terminal failure kinds use the shared
 * palette so every tool speaks the same visual language.
 */
function statusPresentation(
  kind: 'running' | 'awaitingInput' | 'complete' | 'error' | 'cancelled',
  chrome: { color?: Parameters<typeof mantineColorVar>[0]; icon?: StyledIcon },
): { icon: StyledIcon; color: string } {
  if (kind === 'error') {
    return { icon: IconInfoTriangle, color: 'var(--mantine-color-orange-4)' };
  }
  if (kind === 'cancelled') {
    return { icon: IconX, color: 'var(--mantine-color-red-4)' };
  }
  return {
    icon: chrome.icon ?? IconCheck,
    color: chrome.color ? mantineColorVar(chrome.color, TOOL_COLOR_SHADE) : 'var(--mantine-color-green-4)',
  };
}
